import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import type * as Cesium from 'cesium'
import { cdApi } from '../../api/changeDetection'
import { CD_VOXEL_STATUS_LABEL } from '../../types/changeDetection'
import type {
  CdDiff,
  CdDiffCategory,
  CdDiffVisible,
  CdObservation,
  CdProject,
  CdReport,
  CdReportFormat,
  CdVoxelStatus,
} from '../../types/changeDetection'
import CdNewProjectModal from './CdNewProjectModal'
import CdAddObservationModal from './CdAddObservationModal'
import CdDiffPanel from './CdDiffPanel'
import { useCdMapLayers, type CdCompare } from './useCdMapLayers'
import type { CdActiveDiff } from './cdVoxelViz'

export type CdCompareMode = 'blink' | 'split'

interface Props {
  viewerRef: RefObject<Cesium.Viewer | null>
}

const OBS_ACTIVE: CdVoxelStatus[] = ['QUEUED', 'RUNNING']
const DIFF_ACTIVE = new Set(['QUEUED', 'RUNNING'])

const STATUS_STYLE: Record<CdVoxelStatus, string> = {
  NONE: 'bg-slate-100 text-slate-500',
  QUEUED: 'bg-amber-100 text-amber-700',
  RUNNING: 'bg-blue-100 text-blue-700',
  SUCCEEDED: 'bg-emerald-100 text-emerald-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-slate-100 text-slate-500',
}

function toggle(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
}

export default function CdSidebar({ viewerRef }: Props) {
  const [projects, setProjects] = useState<CdProject[]>([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [project, setProject] = useState<CdProject | null>(null)

  const [observations, setObservations] = useState<CdObservation[]>([])
  const [diffs, setDiffs] = useState<CdDiff[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [originalVisibleIds, setOriginalVisibleIds] = useState<number[]>([])
  const [voxelVisibleIds, setVoxelVisibleIds] = useState<number[]>([])
  const [shownDiffId, setShownDiffId] = useState<number | null>(null)
  const [diffVisible, setDiffVisible] = useState<CdDiffVisible>({ add: true, rem: true, chg: true })
  const [reportsByDiff, setReportsByDiff] = useState<Record<number, CdReport[]>>({})
  const [reportBusy, setReportBusy] = useState<Record<string, boolean>>({})
  const [compare, setCompare] = useState<{ diffId: number; mode: CdCompareMode } | null>(null)
  const [splitPos, setSplitPos] = useState(0.5)

  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [editProject, setEditProject] = useState<CdProject | null>(null)
  const [addObsOpen, setAddObsOpen] = useState(false)
  const [gpkgBusy, setGpkgBusy] = useState(false)

  const pollRef = useRef<number | null>(null)
  const gpkgInputRef = useRef<HTMLInputElement | null>(null)

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true)
    try {
      setProjects(await cdApi.listProjects())
    } catch {
      /* ignore */
    } finally {
      setLoadingProjects(false)
    }
  }, [])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  const loadWorkspace = useCallback(async () => {
    if (!project) return
    try {
      const [obs, df] = await Promise.all([
        cdApi.listObservations(project.id),
        cdApi.listDiffs(project.id),
      ])
      setObservations(obs)
      setDiffs(df)
    } catch {
      /* keep previous */
    }
  }, [project])

  useEffect(() => {
    setObservations([])
    setDiffs([])
    setSelected([])
    setOriginalVisibleIds([])
    setVoxelVisibleIds([])
    setShownDiffId(null)
    setDiffVisible({ add: true, rem: true, chg: true })
    setReportsByDiff({})
    setCompare(null)
    loadWorkspace()
  }, [loadWorkspace])

  useEffect(() => {
    const active =
      observations.some((o) => OBS_ACTIVE.includes(o.voxelStatus)) ||
      diffs.some((d) => DIFF_ACTIVE.has(d.status))
    if (active && pollRef.current == null) {
      pollRef.current = window.setInterval(loadWorkspace, 3000)
    } else if (!active && pollRef.current != null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [observations, diffs, loadWorkspace])

  const shownDiff = diffs.find((d) => d.id === shownDiffId) ?? null

  const activeDiff: CdActiveDiff | null = useMemo(() => {
    if (!shownDiff) return null
    const done = shownDiff.items.filter((it) => it.status === 'SUCCEEDED')
    const sum = (pick: (it: CdDiff['items'][number]) => number | null) =>
      done.reduce((acc, it) => acc + (pick(it) ?? 0), 0)
    return {
      diffId: shownDiff.id,
      name: shownDiff.name,
      added: Math.round(sum((it) => it.addedVolume) * 100) / 100,
      removed: Math.round(sum((it) => it.removedVolume) * 100) / 100,
      changed: Math.round(sum((it) => it.changedVolume) * 100) / 100,
      realTilesets: done
        .map((it) => it.resultTilesetUrl)
        .filter((u): u is string => !!u),
      obsIds: [
        ...new Set(
          shownDiff.items.flatMap((it) => [it.sourceObservationId, it.targetObservationId]),
        ),
      ],
      visible: diffVisible,
    }
  }, [shownDiff, diffVisible])

  const compareArg: CdCompare | null = useMemo(() => {
    if (!compare) return null
    const d = diffs.find((x) => x.id === compare.diffId)
    const it = d?.items[0]
    if (!d || d.type !== 'A_B' || !it) return null
    return { mode: compare.mode, sourceObsId: it.sourceObservationId, targetObsId: it.targetObservationId }
  }, [compare, diffs])

  useCdMapLayers({
    viewerRef,
    enabled: !!project,
    observations,
    originalVisibleIds,
    voxelVisibleIds,
    activeDiff,
    center: project?.centerLon != null && project?.centerLat != null
      ? { lon: project.centerLon, lat: project.centerLat }
      : null,
    compare: compareArg,
    splitPosition: splitPos,
  })

  const forget = (id: number) => {
    setOriginalVisibleIds((p) => p.filter((x) => x !== id))
    setVoxelVisibleIds((p) => p.filter((x) => x !== id))
    setSelected((p) => p.filter((x) => x !== id))
  }

  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 2) return [prev[1], id]
      return [...prev, id]
    })

  const toggleVoxel = (id: number) =>
    setVoxelVisibleIds((prev) => {
      const next = toggle(prev, id)
      if (next.includes(id)) setOriginalVisibleIds((o) => (o.includes(id) ? o : [...o, id]))
      return next
    })

  const applyProjectUpdate = (p: CdProject) => {
    setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)))
    setProject((prev) => (prev && prev.id === p.id ? p : prev))
  }

  // ---------- 리포트 ----------

  const loadReports = useCallback(async (diffId: number) => {
    try {
      const list = await cdApi.listReports(diffId)
      setReportsByDiff((prev) => ({ ...prev, [diffId]: list }))
    } catch {
      /* ignore */
    }
  }, [])

  const captureScreenshot = (diffId: number): string | null => {
    if (shownDiffId !== diffId) return null
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return null
    try {
      v.render()
      return v.canvas.toDataURL('image/png')
    } catch {
      return null
    }
  }

  const handleCreateReport = async (diffId: number, format: CdReportFormat) => {
    const key = `${diffId}:${format}`
    setReportBusy((p) => ({ ...p, [key]: true }))
    try {
      await cdApi.createReport(diffId, { format, screenshotBase64: captureScreenshot(diffId) })
      await loadReports(diffId)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? '리포트 생성에 실패했습니다.')
    } finally {
      setReportBusy((p) => ({ ...p, [key]: false }))
    }
  }

  const handleDownloadReport = async (diffId: number, report: CdReport) => {
    try {
      await cdApi.downloadReport(diffId, report.id, report.fileName)
    } catch {
      alert('다운로드에 실패했습니다.')
    }
  }

  const handleDeleteReport = async (diffId: number, reportId: number) => {
    try {
      await cdApi.deleteReport(diffId, reportId)
      await loadReports(diffId)
    } catch {
      alert('삭제에 실패했습니다.')
    }
  }

  const handleGpkgPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !project) return
    setGpkgBusy(true)
    try {
      applyProjectUpdate(await cdApi.uploadRegionGpkg(project.id, file))
    } catch (err: unknown) {
      const x = err as { response?: { data?: { error?: string } } }
      alert(x.response?.data?.error ?? 'GeoPackage 업로드에 실패했습니다.')
    } finally {
      setGpkgBusy(false)
    }
  }

  const handleGpkgRemove = async () => {
    if (!project) return
    setGpkgBusy(true)
    try {
      applyProjectUpdate(await cdApi.deleteRegionGpkg(project.id))
    } catch {
      alert('삭제에 실패했습니다.')
    } finally {
      setGpkgBusy(false)
    }
  }

  // ---------- 프로젝트 목록 화면 ----------
  if (!project) {
    return (
      <div className="flex-1 overflow-y-auto p-3">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900">변화탐지 프로젝트</h2>
          <button
            onClick={() => {
              setEditProject(null)
              setNewProjectOpen(true)
            }}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded transition-colors"
          >
            + 프로젝트 추가
          </button>
        </div>

        {loadingProjects ? (
          <p className="text-xs text-gray-400 px-1">불러오는 중...</p>
        ) : projects.length === 0 ? (
          <div className="text-center text-xs text-gray-400 py-10">
            프로젝트가 없습니다. <span className="text-blue-600">프로젝트 추가</span>로 시작하세요.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {projects.map((p) => (
              <li
                key={p.id}
                onClick={() => setProject(p)}
                className="group border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer transition"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditProject(p)
                        setNewProjectOpen(true)
                      }}
                      className="text-[11px] text-gray-400 hover:text-gray-700"
                    >
                      수정
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (!confirm(`'${p.name}' 프로젝트를 삭제할까요? 하위 관측·결과도 삭제됩니다.`)) return
                        await cdApi.deleteProject(p.id)
                        setProjects((prev) => prev.filter((x) => x.id !== p.id))
                      }}
                      className="text-[11px] text-gray-400 hover:text-red-600"
                    >
                      삭제
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  관측 {p.observationCount}개 · {p.createdAt?.slice(0, 10)}
                </p>
              </li>
            ))}
          </ul>
        )}

        <CdNewProjectModal
          open={newProjectOpen}
          project={editProject}
          onClose={() => setNewProjectOpen(false)}
          onSaved={(saved) =>
            setProjects((prev) => {
              const exists = prev.some((x) => x.id === saved.id)
              return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev]
            })
          }
        />
      </div>
    )
  }

  // ---------- 프로젝트 워크스페이스 화면 ----------
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4">
      <div>
        <button onClick={() => setProject(null)} className="text-xs text-gray-500 hover:text-gray-800">
          ← 프로젝트 목록
        </button>
        <h2 className="mt-1 text-sm font-bold text-gray-900 truncate">{project.name}</h2>

        <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-600">분석 영역(GPKG)</span>
          {project.hasRegionGpkg ? (
            <>
              <span className="text-gray-700 truncate max-w-[150px]" title={project.regionGpkgFileName ?? ''}>
                {project.regionGpkgFileName ?? 'region.gpkg'}
              </span>
              <button
                onClick={handleGpkgRemove}
                disabled={gpkgBusy}
                className="text-red-500 hover:text-red-700 disabled:opacity-40"
              >
                삭제
              </button>
            </>
          ) : (
            <button
              onClick={() => gpkgInputRef.current?.click()}
              disabled={gpkgBusy}
              className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
            >
              {gpkgBusy ? '업로드 중...' : '파일 추가'}
            </button>
          )}
          <input ref={gpkgInputRef} type="file" accept=".gpkg" hidden onChange={handleGpkgPick} />
        </div>
      </div>

      {/* 관측 데이터 */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-bold text-gray-700">관측 데이터 ({observations.length})</h3>
          <button
            onClick={() => setAddObsOpen(true)}
            className="text-[11px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded"
          >
            + 날짜 추가
          </button>
        </div>

        {observations.length === 0 ? (
          <p className="text-[11px] text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg">
            변환 완료 데이터로 관측을 추가하세요.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {observations.map((o) => {
              const showOriginal = originalVisibleIds.includes(o.id)
              const showVoxel = voxelVisibleIds.includes(o.id)
              return (
                <li key={o.id} className="border border-gray-200 rounded-lg px-2.5 py-2">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      disabled={o.voxelStatus !== 'SUCCEEDED'}
                      checked={selected.includes(o.id)}
                      onChange={() => toggleSelect(o.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-gray-800">{o.observedAt}</span>
                        <span className={`text-[10px] px-1 py-0.5 rounded ${STATUS_STYLE[o.voxelStatus]}`}>
                          {CD_VOXEL_STATUS_LABEL[o.voxelStatus]}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 truncate">
                        {o.label ? `${o.label} · ` : ''}
                        {o.meshDataName}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <button
                          onClick={() => setOriginalVisibleIds((prev) => toggle(prev, o.id))}
                          disabled={!o.originalTilesetUrl}
                          className={`text-[10px] px-1.5 py-0.5 rounded border disabled:opacity-40 ${
                            showOriginal ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          지도
                        </button>
                        <button
                          onClick={() => toggleVoxel(o.id)}
                          disabled={o.voxelStatus !== 'SUCCEEDED'}
                          className={`text-[10px] px-1.5 py-0.5 rounded border disabled:opacity-40 ${
                            showVoxel ? 'bg-cyan-600 border-cyan-600 text-white' : 'border-gray-300 text-gray-600'
                          }`}
                        >
                          voxel
                        </button>
                        {(o.voxelStatus === 'FAILED' || o.voxelStatus === 'CANCELLED' || o.voxelStatus === 'NONE') && (
                          <button
                            onClick={async () => {
                              const u = await cdApi.revoxelize(o.id)
                              setObservations((prev) => prev.map((x) => (x.id === o.id ? u : x)))
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-blue-600"
                          >
                            재실행
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            if (!confirm('이 관측을 삭제할까요?')) return
                            await cdApi.deleteObservation(o.id)
                            setObservations((prev) => prev.filter((x) => x.id !== o.id))
                            forget(o.id)
                          }}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-gray-400 hover:text-red-600"
                        >
                          삭제
                        </button>
                      </div>
                      {o.errorMessage && <p className="text-[10px] text-red-600 mt-0.5">{o.errorMessage}</p>}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* 변화탐지 */}
      <CdDiffPanel
        projectId={project.id}
        observations={observations}
        selectedIds={selected}
        diffs={diffs}
        shownDiffId={shownDiffId}
        diffVisible={diffVisible}
        onChanged={loadWorkspace}
        onToggleShow={(id) =>
          setShownDiffId((prev) => {
            const next = prev === id ? null : id
            if (next !== null) setDiffVisible({ add: true, rem: true, chg: true })
            return next
          })
        }
        onToggleDiffVisible={(cat: CdDiffCategory) =>
          setDiffVisible((prev) => ({ ...prev, [cat]: !prev[cat] }))
        }
        reportsByDiff={reportsByDiff}
        reportBusy={reportBusy}
        onLoadReports={loadReports}
        onCreateReport={handleCreateReport}
        onDownloadReport={handleDownloadReport}
        onDeleteReport={handleDeleteReport}
        compare={compare}
        splitPos={splitPos}
        onSetCompare={(diffId, mode) => setCompare(mode === null ? null : { diffId, mode })}
        onSetSplitPos={setSplitPos}
      />

      <CdAddObservationModal
        open={addObsOpen}
        projectId={project.id}
        onClose={() => setAddObsOpen(false)}
        onAdded={(obs) =>
          setObservations((prev) => [...prev, obs].sort((a, b) => a.observedAt.localeCompare(b.observedAt)))
        }
      />
    </div>
  )
}
